import React, { useState, useCallback, lazy, Suspense } from 'react';
import { AnimatePresence, motion as Motion } from 'framer-motion';

// Common Components (Extracted)
import BootstrapScreen from './components/Common/BootstrapScreen';
import LoadingScreen from './components/Common/LoadingScreen';
import PageLoader from './components/Common/PageLoader';
import FirebaseErrorScreen from './components/Common/FirebaseErrorScreen';

// Layout & Navigation (Extracted)
import AdminLayout from './components/Layout/AdminLayout';
import GridLauncher from './components/Layout/GridLauncher';
import BottomNav from './components/Layout/BottomNav';
import EntranceUI from './components/EntranceUI';
import SetupAlertBanner from './components/SetupAlertBanner';
import useNavigationStore from './store/navigationStore';

// Hooks
import useAuth from './hooks/useAuth';
import { LocationProvider } from './context/LocationContext';
import { SecurityProvider } from './context/SecurityContext';

// Lazy Loaded Modules
const FinancialHub = lazy(() => import('./features/FinancialHub'));
const TrendRadar = lazy(() => import('./features/TrendRadar'));
const CompetitorSpy = lazy(() => import('./features/CompetitorSpy'));
const ContentStudio = lazy(() => import('./features/ContentStudio'));
const PromotionFactory = lazy(() => import('./features/PromotionFactory'));
const InventoryManager = lazy(() => import('./features/InventoryManager'));
const CentralInventory = lazy(() => import('./features/CentralInventory'));
const SupplierManager = lazy(() => import('./features/SupplierManager'));
const HRM = lazy(() => import('./features/HRM'));
const StaffPortal = lazy(() => import('./features/HRM/StaffPortal'));
const BrandIdentity = lazy(() => import('./features/BrandIdentity'));
const CRM = lazy(() => import('./features/CRM'));
const KitchenDisplay = lazy(() => import('./features/Kitchen'));
const POSMonitor = lazy(() => import('./features/POSMonitor'));
const Settings = lazy(() => import('./features/Settings'));
const GrowthHub = lazy(() => import('./features/GrowthHub'));
const SupplyChainHub = lazy(() => import('./features/SupplyChainHub'));
const OperationsHub = lazy(() => import('./features/OperationsHub'));
const QRMenu = lazy(() => import('./features/Customer/QRMenu'));
const StaffPublicPortal = lazy(() => import('./features/HRM/StaffPublicPortal'));
const MenuGenerator = lazy(() => import('./features/MenuGenerator'));
const NotificationCenter = lazy(() => import('./features/NotificationCenter/NotificationCenter'));
const POSTerminal = lazy(() => import('./features/POS'));
const ImplementationRoadmap = lazy(() => import('./features/ImplementationRoadmap'));
const MasterLogin = lazy(() => import('./features/MasterDashboard/MasterLogin'));
const MasterDashboard = lazy(() => import('./features/MasterDashboard'));
const MasterLayout = lazy(() => import('./components/Layout/MasterLayout'));
const MasterGridLauncher = lazy(() => import('./components/Layout/MasterGridLauncher'));
const StaffManagement = lazy(() => import('./features/StaffManagement'));
const ForgotPassword = lazy(() => import('./features/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./features/Auth/ResetPassword'));

// Error Boundary
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) return <div className="p-10 text-center text-red-500 font-bold">Lỗi hiển thị hệ thống. Vui lòng tải lại trang.</div>;
    return this.props.children;
  }
}

/**
 * App - The Root Component of F&B OS
 */
function App() {
  const { 
    user, loading, role, ownerId, brandInfo, 
    offlineMode, error, bootstrapRequired, logout, refreshBootstrapStatus,
    setOfflineMode
  } = useAuth();

  const pathname = window.location.pathname;
  const isStaffCheckin = pathname.startsWith('/staff-checkin');
  const isQRMenu = pathname.startsWith('/q/');
  const isPOS = pathname === '/pos' || pathname === '/pos/';
  const isMasterPath = pathname === '/master' || pathname === '/master/';
  const isForgotPassword = pathname === '/forgot-password';
  const isResetPassword = pathname === '/reset-password';
  const { activeTab, isGridView, navigateTo, resetToHub, setInitialState } = useNavigationStore();

  // Initialize navigation state - ONLY ONCE on mount to avoid resetting during interaction
  React.useEffect(() => {
    const tabFromParam = new URLSearchParams(window.location.search).get('tab');
    if (tabFromParam) {
      setInitialState(tabFromParam, false);
    } else if (isPOS) {
      setInitialState('pos', false);
    } else {
      setInitialState(null, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle cross-tab navigation
  const handleNavigate = useCallback((tab) => {
    navigateTo(tab);
  }, [navigateTo]);

  // 0. Navigation State Synchronizer (Handles Reset on Logout/Login)
  const [prevUserUid, setPrevUserUid] = useState(user?.uid);
  if (user?.uid !== prevUserUid) {
    setPrevUserUid(user?.uid);
    resetToHub();
    // Clear URL params on logout
    if (!user && window.location.search.includes('tab=')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  // 1. Loading State
  if (loading) return <LoadingScreen message="Đang khởi động hệ thống..." />;

  // 1.1 Role-Based Redirection (Adjust state during render to avoid cascading useEffect)
  // This ensures that when a Staff/Manager logs in for the first time without a deep link, 
  // they are immediately shown the POS Monitor instead of the Grid Launcher.
  const hasTabInParams = new URLSearchParams(window.location.search).has('tab');
  if (user && role && !activeTab && isGridView && !hasTabInParams) {
    if (role === 'staff' || role === 'manager') {
      navigateTo('pos_monitor');
    }
  }

  // 2. Error State - Allow bypass if offlineMode is active
  if (error && !offlineMode) return <FirebaseErrorScreen errorMessage={error} onSkipLogin={() => setOfflineMode(true)} />;

  // 3. Routing: Public QR Menu
  if (isQRMenu) {
      const parts = pathname.split('/').filter(Boolean);
      return (
        <Suspense fallback={<LoadingScreen />}>
          <QRMenu userId={parts[1]} locationId={parts[2]} tableId={parts[3]} />
        </Suspense>
      );
  }

  // 4. Routing: Staff Portal (Public/Check-in)
  if (isStaffCheckin) {
      return (
        <Suspense fallback={<LoadingScreen />}>
          <StaffPublicPortal />
        </Suspense>
      );
  }

  // 4.0.1 Routing: Forgot Password
  if (isForgotPassword) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ForgotPassword />
      </Suspense>
    );
  }

  // 4.0.2 Routing: Reset Password
  if (isResetPassword) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ResetPassword />
      </Suspense>
    );
  }

  // 4.1 Routing: Master Hidden Portal
  if (isMasterPath && !user) {
    return (
      <Suspense fallback={<LoadingScreen message="Đang khởi tạo Master Portal..." />}>
        <MasterLogin onLoginSuccess={refreshBootstrapStatus} />
      </Suspense>
    );
  }

  // 4.2 Routing: Entrance (Login/Landing) - Show if no user is authenticated
  if (!user) {
    return (
      <Suspense fallback={<LoadingScreen message="Đang tải trang đăng nhập..." />}>
        <EntranceUI onLoginSuccess={refreshBootstrapStatus} />
      </Suspense>
    );
  }

  // 5. Logic: Bootstrap required (New Owner)
  if (bootstrapRequired && user) {
    return <BootstrapScreen user={user} message="Chào mừng bạn! Hãy khởi tạo cửa hàng đầu tiên của mình." onBootstrapSuccess={refreshBootstrapStatus} onLogout={logout} />;
  }

  // 6. Main Application (Authenticated)
  const renderTabContent = () => {
    switch (activeTab) {
      case 'financial': return <FinancialHub user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'pos_monitor': return <POSMonitor user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'kds':
      case 'kitchen': return <KitchenDisplay user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'inventory': return <InventoryManager user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'suppliers': return <SupplierManager user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'central_inventory': return <CentralInventory user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      
      case 'hrm': return <HRM user={user} ownerId={ownerId} isOwner={role === 'owner'} onNavigate={handleNavigate} />;
      case 'brand': return <BrandIdentity user={user} ownerId={ownerId} isOwner={role === 'owner'} onBrandUpdate={() => {}} />;
      case 'crm': return <CRM user={user} ownerId={ownerId} isOwner={role === 'owner'} onNavigate={handleNavigate} />;
      
      case 'trends': return <TrendRadar user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'competitor': return <CompetitorSpy user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'content': return <ContentStudio user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'promo_factory': return <PromotionFactory user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'menu_generator': return <MenuGenerator user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
 
      case 'settings': return <Settings user={user} ownerId={ownerId} isOwner={role === 'owner'} onNavigate={handleNavigate} />;
      case 'staff_management': return <StaffManagement user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'staff_portal': return <StaffPortal user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'pos': return <POSTerminal user={user} ownerId={ownerId} isOwner={role === 'owner'} onNavigate={handleNavigate} onLogout={logout} />;
      case 'notifications': return <NotificationCenter user={user} ownerId={ownerId} onBack={resetToHub} />;
      
      // Specialized Hubs
      case 'growth_hub': return <GrowthHub user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'supply_chain_hub': return <SupplyChainHub user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'operations_hub': return <OperationsHub user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      case 'implementation_roadmap': return <ImplementationRoadmap user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
      
      // Master Tabs
      case 'master_dashboard':
      case 'owner_management':
      case 'platform_analytics':
      case 'ml_pipeline':
      case 'system_logs':
      case 'database_ops':
      case 'system_health':
      case 'system_settings':
        return <MasterDashboard activeTab={activeTab} />;

      default: 
        if (role === 'master') return <MasterDashboard />;
        return <FinancialHub user={user} ownerId={ownerId} onNavigate={handleNavigate} />;
    }
  };

  // 6.2 Master Application (Specialized Layout)
  if (role === 'master') {
    return (
      <SecurityProvider>
        <MasterLayout
          user={user}
          activeTab={activeTab}
          setActiveTab={navigateTo}
          handleLogout={logout}
          isGridView={isGridView}
          setIsGridView={(val) => val ? resetToHub() : null}
        >
          {isGridView ? (
            <MasterGridLauncher onTabChange={handleNavigate} />
          ) : (
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <div className="relative z-10 entrance-glass rounded-3xl p-8 border border-white/10 shadow-2xl min-h-[calc(100vh-16rem)]">
                   {renderTabContent()}
                </div>
              </Suspense>
            </ErrorBoundary>
          )}
        </MasterLayout>
      </SecurityProvider>
    );
  }

  // 6.3 Standard Application (Authenticated)
  return (
    <SecurityProvider>
      <LocationProvider user={user} ownerIdForLocations={ownerId}>
        <AdminLayout
          activeTab={activeTab}
          setActiveTab={navigateTo}
          user={user}
          offlineMode={offlineMode}
          handleLogout={logout}
          isOwner={role === 'owner'}
          brandInfo={brandInfo}
          isKioskMode={activeTab === 'pos'}
          isGridView={isGridView}
          setIsGridView={(val) => val ? resetToHub() : null}
          footer={
            <BottomNav
              activeTab={isGridView ? null : activeTab}
              onTabChange={handleNavigate}
              onGoHome={resetToHub}
              role={role}
            />
          }
        >
          <SetupAlertBanner userId={user?.uid} />
          {isGridView ? (
            <Motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <GridLauncher user={user} isOwner={role === 'owner'} onTabChange={handleNavigate} />
            </Motion.div>
          ) : (
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <div className="relative z-10 entrance-glass rounded-3xl p-6 border border-white/10 shadow-2xl min-h-[calc(100vh-12rem)]">
                  {renderTabContent()}
                </div>
              </Suspense>
            </ErrorBoundary>
          )}
        </AdminLayout>
      </LocationProvider>
    </SecurityProvider>
  );
}

export default App;
